import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, auth } from './client';
import type {
  Employee,
  Schedule,
  Appointment,
  AppointmentCatalogItem,
  EligibleEmployee,
  Absence,
  AbsenceType,
  OrganizationProposal,
  ContractType,
  EmployeePosition,
  Skill,
  EmployeeUnavailability,
  ScheduleMode,
  VariableFillPlan,
  VariableStaffMember,
  AppNotification,
  WeeklyTemplateDay,
} from '@/types';
import type { OpeningDay } from '@/lib/schedule';

// ============================================================================
// EMPLOYEES
// ============================================================================

export function useEmployees(opts?: { archived?: boolean }) {
  const archived = opts?.archived ?? false;
  return useQuery({
    queryKey: ['employees', { archived }],
    queryFn: () =>
      api
        .get<{ employees: Employee[] }>(`/api/employees${archived ? '?archived=true' : ''}`)
        .then((r) => r.employees),
  });
}

export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: ['employees', id],
    queryFn: () => api.get<{ employee: Employee }>(`/api/employees/${id}`).then((r) => r.employee),
    enabled: !!id,
  });
}

interface CreateEmployeeInput {
  firstName: string;
  lastName: string;
  position: EmployeePosition;
  // Optionnels pour un titulaire (isOwner) : pas de contrat/heures à suivre.
  contractType?: ContractType;
  contractHours?: number;
  isOwner?: boolean;
  hireDate: string;
  endDate?: string;
  replacesEmployeeId?: string;
  phone?: string;
  email?: string;
  skills?: Skill[];
  /** Solde d'heures repris à l'ouverture du compte (reprise de l'existant). */
  openingHoursBalance?: number;
  constraints?: {
    fixedDayOff?: number;
    minStartTime?: string;
    maxEndTime?: string;
    fixedSchedule?: boolean;
  };
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEmployeeInput) =>
      api.post<{ employee: Employee }>('/api/employees', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useUpdateEmployee(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CreateEmployeeInput> & { color?: string; archived?: boolean }) =>
      api.patch<{ employee: Employee }>(`/api/employees/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employees', id] });
      // Heures contractuelles et « concerné par la fiche navette » changent
      // la navette, la balance et les stats.
      qc.invalidateQueries({ queryKey: ['shuttle'] });
      qc.invalidateQueries({ queryKey: ['hours-balance'] });
      qc.invalidateQueries({ queryKey: ['statistics'] });
    },
  });
}

// Suppression DÉFINITIVE d'un salarié (R12) — irréversible.
export function useDeleteEmployeePermanent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/employees/${id}/permanent`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

interface EmployeeCounters {
  counters: {
    month: { plannedHours: number; contractHours: number; overtimeHours: number };
    leaves: { quotaYearly: number; takenYtd: number; remaining: number };
    sickLast12Months: number;
    hoursBalance: number;
  };
}

export function useEmployeeCounters(id: string | undefined) {
  return useQuery({
    queryKey: ['employees', id, 'counters'],
    queryFn: () => api.get<EmployeeCounters>(`/api/employees/${id}/counters`).then((r) => r.counters),
    enabled: !!id,
  });
}

// ============================================================================
// UNAVAILABILITIES (indisponibilités ponctuelles — "jours random")
// ============================================================================

export function useUnavailabilitiesRange(from: string, to: string) {
  return useQuery({
    queryKey: ['unavailabilities', 'range', from, to],
    queryFn: () => {
      const params = new URLSearchParams({ from, to });
      return api
        .get<{ unavailabilities: EmployeeUnavailability[] }>(
          `/api/employees/unavailabilities/range?${params}`
        )
        .then((r) => r.unavailabilities);
    },
  });
}

export function useEmployeeUnavailabilities(id: string | undefined, year: number) {
  return useQuery({
    queryKey: ['employees', id, 'unavailabilities', year],
    queryFn: () =>
      api
        .get<{ unavailabilities: EmployeeUnavailability[] }>(
          `/api/employees/${id}/unavailabilities?year=${year}`
        )
        .then((r) => r.unavailabilities),
    enabled: !!id,
  });
}

export function useSetUnavailability(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      date: string;
      type: AbsenceType;
      note?: string | null;
      startTime?: string | null;
      endTime?: string | null;
    }) =>
      api.post<{ unavailability: EmployeeUnavailability }>(
        `/api/employees/${id}/unavailabilities`,
        data
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees', id, 'unavailabilities'] });
      qc.invalidateQueries({ queryKey: ['unavailabilities', 'range'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

export function useDeleteUnavailability(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uid: string) => api.delete(`/api/employees/${id}/unavailabilities/${uid}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees', id, 'unavailabilities'] });
      qc.invalidateQueries({ queryKey: ['unavailabilities', 'range'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

// ============================================================================
// WEEKLY TEMPLATE (semaine type / disponibilités hebdomadaires)
// ============================================================================

interface WeeklyTemplateResponse {
  scheduleMode: ScheduleMode;
  template: WeeklyTemplateDay[];
  /** Multi-site (lot 3B) — présents dès que le salarié est partagé. */
  multiSite?: boolean;
  /** Pharmacie de rattachement : c'est elle que représente un `pharmacyId` nul. */
  homePharmacyId?: string;
  /** Autres officines où il travaille. */
  sites?: { id: string; name: string }[];
  /**
   * Horaires d'ouverture par officine (clé = `pharmacyId`), rattachement inclus.
   * Le pré-remplissage de la semaine type s'en sert plutôt que d'horaires écrits
   * en dur, et n'y propose le dimanche que si l'officine ouvre ce jour-là.
   */
  openingHours?: Record<string, OpeningDay[]>;
  /**
   * Repos fixes hebdomadaires (0 = dimanche … 6 = samedi), réglés dans
   * « Changer les horaires ». Ils vident le jour quels que soient les horaires
   * de la semaine type — la fiche doit donc les montrer.
   */
  fixedDaysOff?: number[];
  /** Journées à venir réglées à la main sur le planning, qui ne suivent plus la semaine type. */
  manualDays?: ManualDay[];
}

export interface ManualDay {
  date: string; // YYYY-MM-DD
  /** `false` = journée retirée à la main (ne travaille pas). */
  worked: boolean;
  morningStart: string | null;
  morningEnd: string | null;
  afternoonStart: string | null;
  afternoonEnd: string | null;
  siteId: string;
  afternoonSiteId: string;
}

interface SaveWeeklyTemplateResponse extends WeeklyTemplateResponse {
  /** Repos fixes levés : la semaine type enregistrée déclarait ce jour travaillé. */
  liftedDaysOff?: number[];
  /** Journées figées à la main remises sur la semaine type par l'enregistrement. */
  reclaimedManual?: number;
}

export function useWeeklyTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ['employees', id, 'weekly-template'],
    queryFn: () =>
      api.get<WeeklyTemplateResponse>(`/api/employees/${id}/weekly-template`),
    enabled: !!id,
  });
}

export function useSaveWeeklyTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { scheduleMode?: ScheduleMode; rows: WeeklyTemplateDay[] }) =>
      api.put<SaveWeeklyTemplateResponse>(`/api/employees/${id}/weekly-template`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees', id, 'weekly-template'] });
      qc.invalidateQueries({ queryKey: ['employees', id] });
      qc.invalidateQueries({ queryKey: ['employees'] });
      // La semaine type recalcule le planning côté serveur → on rafraîchit.
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

/**
 * « Remettre sur la semaine type » : efface les journées à venir réglées à la
 * main qui ne la suivent plus, et le planning se re-dérive de la semaine type.
 */
export function useReclaimManualDays(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ reclaimed: number }>(`/api/employees/${id}/manual-days`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees', id, 'weekly-template'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

// ============================================================================
// SETTINGS
// ============================================================================

// ============================================================================
// MULTI-SITE (M9-3, lot 3A)
// ============================================================================

export interface Site {
  id: string;
  name: string;
  city: string | null;
  /** L'officine ouvre-t-elle le dimanche ? La semaine affichée gagne alors un 7e jour. */
  opensOnSunday?: boolean;
}

/**
 * Les officines du groupe et celle qui est active. `multiSite` passe à `true`
 * dès la deuxième pharmacie : c'est l'ajout d'un site qui « active » le
 * multi-site, il n'y a pas de réglage à cocher.
 */
export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      const res = await api.get<{ sites: Site[]; activeSiteId: string; multiSite: boolean }>(
        '/api/pharmacy/sites'
      );
      // Le serveur a le dernier mot : si l'officine mémorisée dans ce navigateur
      // n'est plus servie (site retiré du groupe, autre compte), on se recale
      // sur celle qu'il annonce plutôt que de renvoyer indéfiniment un en-tête
      // qu'il ignore.
      auth.setSite(res.activeSiteId);
      return res;
    },
  });
}

/**
 * Multi-site (lot 3C) — les mêmes données, mais pour une AUTRE officine du
 * groupe, sans basculer dessus. Chaque vue empilée de l'onglet « Gestion
 * multi-site » appelle ces hooks une fois par pharmacie.
 */
export function useSiteSchedules(siteId: string, from: string, to: string) {
  return useQuery({
    queryKey: ['sites', siteId, 'schedules', from, to],
    queryFn: () =>
      api.get<{ schedules: Schedule[] }>(
        `/api/schedules?from=${from}&to=${to}&siteId=${siteId}`
      ).then((r) => r.schedules),
    enabled: !!siteId && !!from && !!to,
  });
}

export function useSiteEmployees(siteId: string) {
  return useQuery({
    queryKey: ['sites', siteId, 'employees'],
    queryFn: () =>
      api.get<{ employees: Employee[] }>(`/api/employees?siteId=${siteId}`).then((r) => r.employees),
    enabled: !!siteId,
  });
}

export function useSiteAppointments(siteId: string, from: string, to: string) {
  return useQuery({
    queryKey: ['sites', siteId, 'appointments', from, to],
    queryFn: () =>
      api.get<{ appointments: Appointment[] }>(
        `/api/appointments?from=${from}&to=${to}&siteId=${siteId}`
      ).then((r) => r.appointments),
    enabled: !!siteId && !!from && !!to,
  });
}

export function useSiteOrganization(siteId: string, date: string, morning: boolean) {
  return useQuery({
    queryKey: ['sites', siteId, 'organization', date, morning],
    queryFn: () =>
      api.get<{ proposal: OrganizationProposal }>(
        `/api/organization?date=${date}&morning=${morning}&siteId=${siteId}`
      ).then((r) => r.proposal),
    enabled: !!siteId && !!date,
  });
}

/**
 * Horaires d'ouverture d'une AUTRE officine du groupe. La vue d'ensemble en a
 * besoin pour afficher ses plannings comme le fait l'écran Planning : sans eux,
 * impossible de distinguer une demi-journée fermée d'une demi-journée non
 * travaillée.
 */
export function useSitePharmacy(siteId: string) {
  return useQuery({
    queryKey: ['sites', siteId, 'pharmacy'],
    queryFn: () =>
      api.get<{ pharmacy: any }>(`/api/settings/pharmacy?siteId=${siteId}`).then((r) => r.pharmacy),
    enabled: !!siteId,
  });
}

export function useSiteHoursBalance(siteId: string, months = 12) {
  return useQuery({
    queryKey: ['sites', siteId, 'hours-balance', months],
    queryFn: () =>
      api.get<any>(`/api/statistics/hours-balance?months=${months}&siteId=${siteId}`),
    enabled: !!siteId,
  });
}

/** Ajoute une officine entière au groupe (titulaire). */
export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      shuttleEmail: string;
      address?: string;
      city?: string;
      postalCode?: string;
      teamPin: string;
    }) => api.post<{ site: Site }>('/api/pharmacy/sites', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

/**
 * Bascule la pharmacie active, puis **recharge complètement la page**.
 *
 * On mémorise l'officine des DEUX façons :
 * - le jeton renvoyé par le serveur, positionné dessus (login classique) ;
 * - `auth.setSite()`, envoyé en en-tête `X-Site-Id` à chaque appel.
 *
 * Le second n'est pas une ceinture et des bretelles : sur `rh.pharmagestion.fr`
 * (SSO Clerk) le jeton de bascule est écrasé par un jeton Clerk frais dès la
 * requête suivante. Sans l'en-tête, la bascule « clignotait » et l'écran
 * revenait sur la pharmacie principale.
 *
 * **Pourquoi un rechargement et plus un `qc.clear()`.** Retour du boss :
 * « quand je change de pharmacie ça charge pas le nouveau planning » — il
 * fallait un F5 à la main. Deux raisons, et vider le cache n'en règle aucune :
 *
 *   1. `clear()` supprime les entrées du cache mais ne relance pas les écrans
 *      déjà montés : le planning restait sur ce qu'il avait, ou sur rien ;
 *   2. surtout, les requêtes **déjà parties** l'ont été avec l'ANCIEN en-tête
 *      `X-Site-Id`. Leur réponse arrive APRÈS le vidage et repeuple le cache
 *      avec les données de l'officine qu'on vient de quitter. Aucun ménage
 *      côté client ne peut rattraper ça — c'est une course, pas un cache sale.
 *
 * Un rechargement tranche les deux d'un coup : plus une seule requête en vol,
 * et tout l'écran repart du nouveau jeton. C'est déjà ce qu'on fait pour la
 * bascule du poste comptoir, et pour la même raison — le jeton change.
 *
 * @param vars l'officine à ouvrir, et où atterrir (la page courante par défaut).
 */
export function useSwitchSite() {
  return useMutation({
    mutationFn: (vars: string | { siteId: string; to?: string }) => {
      const siteId = typeof vars === 'string' ? vars : vars.siteId;
      return api.post<{ token: string; site: Site }>(`/api/pharmacy/sites/${siteId}/switch`);
    },
    onSuccess: (res, vars) => {
      auth.setToken(res.token);
      auth.setSite(res.site.id);
      const to =
        (typeof vars === 'string' ? undefined : vars.to) ??
        window.location.pathname + window.location.search;
      window.location.assign(to);
    },
  });
}

export function usePharmacySettings() {
  return useQuery({
    queryKey: ['settings', 'pharmacy'],
    queryFn: () => api.get<{ pharmacy: any }>('/api/settings/pharmacy').then((r) => r.pharmacy),
  });
}

export function useUpdatePharmacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name?: string;
      siret?: string | null;
      shuttleEmail?: string;
      address?: string | null;
      city?: string | null;
      postalCode?: string | null;
      /** Renforts max par demi-journée pour le comblement des horaires variables. */
      maxReinforcementsPerHalfDay?: number;
      /**
       * Début d'utilisation du logiciel (« yyyy-MM-dd »), `null` pour l'ôter.
       * Les journées antérieures sont effacées côté serveur (hors semaines
       * validées) — d'où `purged` dans la réponse.
       */
      usageStartDate?: string | null;
    }) => api.patch<{ pharmacy: any; purged: number }>('/api/settings/pharmacy', data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      // Le début d'utilisation efface et re-borne des journées : tout ce qui
      // les lit doit se recharger.
      if (vars.usageStartDate !== undefined) {
        qc.invalidateQueries({ queryKey: ['schedules'] });
        qc.invalidateQueries({ queryKey: ['coverage'] });
        qc.invalidateQueries({ queryKey: ['hours-balance'] });
        qc.invalidateQueries({ queryKey: ['shuttle'] });
      }
    },
  });
}

export function useUpdateTeamPin() {
  return useMutation({
    mutationFn: (pin: string) => api.patch('/api/settings/team-pin', { pin }),
  });
}

/**
 * Enregistre les horaires d'ouverture.
 * La réponse indique s'il faut proposer un recalcul GÉNÉRAL du personnel en
 * horaires variables : changer les horaires déplace les creux sur toute la
 * période à venir, pas seulement sur les créneaux directement ajustés.
 */
export interface OpeningHoursResult {
  adjustedSlots?: number;
  affectedEmployees?: number;
  affectedNames?: string[];
  variableStaff: VariableStaffMember[];
  suggestVariableRecompute: boolean;
  recomputeRange: { from: string; to: string };
}

export function useUpdateOpeningHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hours: any[]) =>
      api.put<OpeningHoursResult>('/api/settings/opening-hours', { hours }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
    },
  });
}

export function useUpdateStaffingRequirements() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requirements: any[]) =>
      api.put('/api/settings/staffing-requirements', { requirements }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
    },
  });
}

// --- Gardes ----------------------------------------------------------------

/** Volet ouvert (accueil normal) ou fermé (service au guichet). */
export type DutyShutter = 'OPEN' | 'CLOSED';

export interface DutyAssignment {
  id: string;
  employeeId: string;
  startTime: string;
  endTime: string;
  /** Durée réelle, passage de minuit compris — calculée par le serveur. */
  hours: number;
  overnight: boolean;
  employee: { id: string; firstName: string; lastName: string; color: string | null; position: string };
}

export interface Duty {
  id: string;
  /** Jour où la garde COMMENCE (YYYY-MM-DD). */
  date: string;
  shutter: DutyShutter;
  startTime: string;
  endTime: string;
  notes: string | null;
  /** La garde finit le lendemain. */
  overnight: boolean;
  hours: number;
  assignments: DutyAssignment[];
}

export function useDuties(from: string, to: string) {
  return useQuery({
    queryKey: ['duties', from, to],
    queryFn: () => {
      const params = new URLSearchParams({ from, to });
      return api.get<{ duties: Duty[] }>(`/api/duties?${params}`).then((r) => r.duties);
    },
  });
}

/**
 * Toutes les mutations de garde invalident `duties`, mais aussi les heures :
 * une garde compte dans la balance et sur la fiche navette. Ne pas les
 * invalider laisserait un total périmé à l'écran juste après la saisie.
 */
function useDutyMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['duties'] });
      qc.invalidateQueries({ queryKey: ['hours-balance'] });
      qc.invalidateQueries({ queryKey: ['shuttle'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useCreateDuty() {
  return useDutyMutation((body: {
    date: string;
    shutter: DutyShutter;
    startTime: string;
    endTime: string;
    notes?: string | null;
  }) => api.post<{ duty: Duty }>('/api/duties', body));
}

export function useUpdateDuty() {
  return useDutyMutation(({ id, ...body }: {
    id: string;
    date?: string;
    shutter?: DutyShutter;
    startTime?: string;
    endTime?: string;
    notes?: string | null;
  }) => api.patch<{ duty: Duty }>(`/api/duties/${id}`, body));
}

export function useDeleteDuty() {
  return useDutyMutation((id: string) => api.delete(`/api/duties/${id}`));
}

export function useAssignDuty() {
  return useDutyMutation(({ dutyId, ...body }: {
    dutyId: string;
    employeeId: string;
    startTime?: string;
    endTime?: string;
  }) => api.post<{ duty: Duty }>(`/api/duties/${dutyId}/assignments`, body));
}

export function useUpdateDutyAssignment() {
  return useDutyMutation(({ id, ...body }: { id: string; startTime?: string; endTime?: string }) =>
    api.patch<{ duty: Duty }>(`/api/duties/assignments/${id}`, body)
  );
}

export function useRemoveDutyAssignment() {
  return useDutyMutation((id: string) => api.delete(`/api/duties/assignments/${id}`));
}

export function useHolidays() {
  return useQuery({
    queryKey: ['settings', 'holidays'],
    queryFn: () => api.get<{ holidays: any[] }>('/api/settings/holidays').then((r) => r.holidays),
  });
}

export function useSeedFrenchHolidays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) => api.post<{ holidays: any[]; createdCount: number }>('/api/settings/holidays/seed-france', { year }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export function useCreateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { date: string; name: string; isOpen?: boolean; payMultiplier?: number }) =>
      api.post('/api/settings/holidays', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export function useUpdateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; isOpen?: boolean; payMultiplier?: number; name?: string }) =>
      api.patch(`/api/settings/holidays/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/settings/holidays/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export function usePrimes() {
  return useQuery({
    queryKey: ['settings', 'primes'],
    queryFn: () => api.get<{ primes: any[] }>('/api/settings/primes').then((r) => r.primes),
  });
}

export function useCreatePrime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; amount?: number | null; amountFormula?: string | null; periodicity: 'monthly' | 'annual' | 'per_event'; active?: boolean }) =>
      api.post('/api/settings/primes', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'primes'] }),
  });
}

export function useUpdatePrime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; [k: string]: any }) =>
      api.patch(`/api/settings/primes/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'primes'] }),
  });
}

export function useDeletePrime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/settings/primes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'primes'] }),
  });
}

/**
 * Accès en consultation de l'officine active : une adresse PharmaGestion qui
 * voit l'écran du poste comptoir (emploi du temps, organisation, rendez-vous).
 */
export interface ViewerAccess {
  id: string;
  email: string;
  createdAt: string;
}

export function useViewers() {
  return useQuery({
    queryKey: ['settings', 'viewers'],
    queryFn: () => api.get<{ viewers: ViewerAccess[] }>('/api/settings/viewers').then((r) => r.viewers),
  });
}

export function useAddViewer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api.post<{ viewer: ViewerAccess }>('/api/settings/viewers', { email }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'viewers'] }),
  });
}

export function useRemoveViewer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/settings/viewers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'viewers'] }),
  });
}

export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/employees/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

// ============================================================================
// SCHEDULES
// ============================================================================

/**
 * `elsewhere` : ajoute les journées faites ENTIÈREMENT dans une autre officine
 * du groupe (sans leurs horaires), pour que le planning écrive « → Palais » au
 * lieu d'une case vide. Seul le planning le demande.
 */
export function useSchedules(from: string, to: string, employeeId?: string, opts?: { elsewhere?: boolean }) {
  const elsewhere = !!opts?.elsewhere;
  return useQuery({
    queryKey: ['schedules', from, to, employeeId, elsewhere],
    queryFn: () => {
      const params = new URLSearchParams({
        from,
        to,
        ...(employeeId ? { employeeId } : {}),
        ...(elsewhere ? { elsewhere: '1' } : {}),
      });
      return api
        .get<{ schedules: Schedule[] }>(`/api/schedules?${params}`)
        .then((r) => r.schedules);
    },
  });
}

interface UpsertScheduleInput {
  employeeId: string;
  date: string;
  morningStart?: string | null;
  morningEnd?: string | null;
  afternoonStart?: string | null;
  afternoonEnd?: string | null;
  status: string;
  notes?: string;
}

export function useUpsertSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpsertScheduleInput) =>
      api.put<{ schedule: Schedule }>(`/api/schedules/${data.employeeId}/${data.date}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

/**
 * « Basculer à la pharmacie… » : déplace UNE journée dans une autre officine du
 * groupe, horaires inchangés. `addedSiteIds` = officines où le salarié vient
 * d'être rattaché pour que la journée y soit visible.
 */
export function useSwitchScheduleSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { employeeId: string; date: string; morningSiteId?: string; afternoonSiteId?: string }) =>
      api.put<{ schedule: Schedule; addedSiteIds: string[] }>(
        `/api/schedules/${data.employeeId}/${data.date}/site`,
        { morningSiteId: data.morningSiteId, afternoonSiteId: data.afternoonSiteId }
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
      // La vue « Gestion multi-site » lit chaque officine sous ['sites', id, …].
      qc.invalidateQueries({ queryKey: ['sites'] });
      if (res.addedSiteIds.length > 0) qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

/** Retire une saisie manuelle : le jour se re-dérive de la semaine type. */
export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { employeeId: string; date: string }) =>
      api.delete(`/api/schedules/${data.employeeId}/${data.date}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

interface GenerateResult {
  upserted: number;
  skippedLocked: number;
  skippedException: number;
}

// Génère le planning d'une période à partir des semaines types des salariés.
export function useGenerateSchedules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { from: string; to: string }) =>
      api.post<{ result: GenerateResult }>('/api/schedules/generate', data).then((r) => r.result),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

// ============================================================================
// APPOINTMENTS
// ============================================================================

export function useAppointments(from: string, to: string) {
  return useQuery({
    queryKey: ['appointments', from, to],
    queryFn: () => {
      const params = new URLSearchParams({ from, to });
      return api
        .get<{ appointments: Appointment[] }>(`/api/appointments?${params}`)
        .then((r) => r.appointments);
    },
  });
}

interface CreateAppointmentInput {
  /** Prestation du catalogue. Porte la compétence requise et la durée par défaut. */
  catalogItemId: string;
  startAt: string;
  durationMinutes?: number;
  patientName?: string;
  patientPhone?: string;
  notes?: string;
  /** Désignation manuelle. Si la personne n'est pas qualifiée, `forcedReason` devient obligatoire. */
  forcedEmployeeId?: string;
  forcedReason?: string;
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAppointmentInput) =>
      api.post<{ appointment: Appointment }>('/api/appointments', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  });
}

interface CandidatesResult {
  requiredSkill: Skill | null;
  /** Prestation réservée à cette personne, le cas échéant. */
  pinnedEmployeeId: string | null;
  /** Présents, compétents et libres sur le créneau. */
  eligible: EligibleEmployee[];
  /** Présents et libres mais ne remplissant pas la condition — cible d'une désignation exceptionnelle. */
  fallback: EligibleEmployee[];
}

/**
 * Qui peut prendre ce RDV, avant même de le créer. Permet d'afficher le rouge
 * dans le formulaire au lieu de le découvrir après validation.
 */
export function useAppointmentCandidates(
  params: {
    catalogItemId: string;
    startAt: string;
    durationMinutes: number;
    excludeId?: string;
  } | null
) {
  return useQuery({
    queryKey: ['appointment-candidates', params],
    enabled: !!params,
    queryFn: () => {
      const p = new URLSearchParams({
        catalogItemId: params!.catalogItemId,
        startAt: params!.startAt,
        durationMinutes: String(params!.durationMinutes),
      });
      if (params!.excludeId) p.set('excludeId', params!.excludeId);
      return api.get<CandidatesResult>(`/api/appointments/candidates?${p}`);
    },
  });
}

// ---- Catalogue des prestations ----

/** `all` = inclut les prestations désactivées (écran de réglages). */
export function useAppointmentCatalog(all = false) {
  return useQuery({
    queryKey: ['appointment-catalog', { all }],
    queryFn: () =>
      api
        .get<{ items: AppointmentCatalogItem[] }>(
          `/api/appointments/catalog${all ? '?all=true' : ''}`
        )
        .then((r) => r.items),
  });
}

interface CatalogItemInput {
  label: string;
  requiredSkill?: Skill | null;
  category?: string | null;
  defaultDurationMinutes: number;
  defaultEmployeeId?: string | null;
  active?: boolean;
}

export function useCreateCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CatalogItemInput) =>
      api.post<{ item: AppointmentCatalogItem }>('/api/appointments/catalog', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointment-catalog'] }),
  });
}

export function useUpdateCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<CatalogItemInput> & { id: string }) =>
      api.patch<{ item: AppointmentCatalogItem }>(`/api/appointments/catalog/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointment-catalog'] });
      // La compétence d'une prestation change les verdicts des RDV à venir.
      qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export function useDeleteCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deactivated: boolean; usedBy: number }>(`/api/appointments/catalog/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointment-catalog'] });
      qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/** Validation à titre exceptionnel : désigne quelqu'un malgré l'alerte, avec justification. */
export function useForceAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      employeeId,
      forcedReason,
    }: {
      id: string;
      employeeId: string;
      forcedReason: string;
    }) =>
      api.patch<{ appointment: Appointment }>(`/api/appointments/${id}/force`, {
        employeeId,
        forcedReason,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  });
}

export function useCancelAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/appointments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  });
}

// ============================================================================
// ABSENCES
// ============================================================================

export function useAbsences() {
  return useQuery({
    queryKey: ['absences'],
    queryFn: () => api.get<{ absences: Absence[] }>('/api/absences').then((r) => r.absences),
  });
}

export function useCreateAbsence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      employeeId: string;
      type: string;
      startDate: string;
      endDate: string;
      comment?: string;
    }) => api.post<{ absence: Absence }>('/api/absences', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['absences'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

export function useDeleteAbsence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/absences/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['absences'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}

// ============================================================================
// ORGANIZATION (moteur)
// ============================================================================

export function useOrganization(date: string, morning: boolean) {
  return useQuery({
    queryKey: ['organization', date, morning],
    queryFn: () => {
      const params = new URLSearchParams({ date, morning: String(morning) });
      return api
        .get<{ proposal: OrganizationProposal }>(`/api/organization?${params}`)
        .then((r) => r.proposal);
    },
  });
}

// --- Couverture horaire (timeline 30 min) ---------------------------------

export interface TimelineMission {
  mission: string;
  have: number;
  minCount: number;
  targetCount: number;
  status: 'COVERED' | 'PARTIAL' | 'UNCOVERED';
}
export interface TimelineSegment {
  from: string;
  to: string;
  presentCount: number;
  missions: TimelineMission[];
  worst: 'COVERED' | 'PARTIAL' | 'UNCOVERED';
}
export interface TimelineGap {
  mission: string;
  from: string;
  to: string;
  have: number;
  minCount: number;
  targetCount: number;
  level: 'CRITICAL' | 'WARNING';
}
export interface HalfDayTimeline {
  isMorning: boolean;
  closed: boolean;
  open: string | null;
  close: string | null;
  slotMinutes: number;
  segments: TimelineSegment[];
  gaps: TimelineGap[];
  presentLow: number;
  presentPeak: number;
  /** AUTO = répartition du moteur ; MANUAL = celle faite à la main (glisser-déposer). */
  basis?: 'AUTO' | 'MANUAL';
}

export function useOrgTimeline(date: string, morning: boolean) {
  return useQuery({
    queryKey: ['organization', 'timeline', date, morning],
    queryFn: () => {
      const params = new URLSearchParams({ date, morning: String(morning) });
      return api
        .get<{ timeline: HalfDayTimeline }>(`/api/organization/timeline?${params}`)
        .then((r) => r.timeline);
    },
  });
}

export function useAssignOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      date: string;
      isMorning: boolean;
      assignments: { mission: string; employeeIds: string[] }[];
    }) => api.put('/api/organization/assign', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organization'] }),
  });
}

export function useResetOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { date: string; isMorning: boolean }) => {
      const params = new URLSearchParams({ date: data.date, isMorning: String(data.isMorning) });
      return api.delete(`/api/organization/assign?${params}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organization'] }),
  });
}

// ============================================================================
// COVERAGE (couverture : dispos réelles vs effectifs requis)
// ============================================================================

export type CoverageStatus = 'COVERED' | 'PARTIAL' | 'UNCOVERED' | 'CLOSED';

export interface CoverageAlert {
  type: 'CRITICAL' | 'WARNING' | 'INFO';
  message: string;
  mission?: string;
}

export interface HalfDayCoverage {
  isMorning: boolean;
  status: CoverageStatus;
  presentCount: number;
  alerts: CoverageAlert[];
}

export interface DayCoverage {
  date: string; // yyyy-mm-dd
  dayOfWeek: number;
  morning: HalfDayCoverage;
  afternoon: HalfDayCoverage;
  worst: CoverageStatus;
}

export function useCoverageRange(from: string, to: string) {
  return useQuery({
    queryKey: ['coverage', from, to],
    queryFn: () => {
      const params = new URLSearchParams({ from, to });
      return api.get<{ days: DayCoverage[] }>(`/api/coverage?${params}`).then((r) => r.days);
    },
  });
}

// ============================================================================
// TEAM (mode poste partagé)
// ============================================================================

export function useTeamToday() {
  return useQuery({
    queryKey: ['team', 'today'],
    queryFn: () =>
      api.get<{
        date: string;
        morning: OrganizationProposal;
        afternoon: OrganizationProposal;
      }>('/api/team/today'),
    staleTime: 60 * 1000, // 1 min
  });
}

export function useTeamAgenda(from: string, to: string) {
  return useQuery({
    queryKey: ['team', 'agenda', from, to],
    queryFn: () => {
      const params = new URLSearchParams({ from, to });
      return api
        .get<{ appointments: Appointment[] }>(`/api/team/agenda?${params}`)
        .then((r) => r.appointments);
    },
  });
}

// ============================================================================
// STATISTICS
// ============================================================================

interface HeadcountDay {
  date: string;
  dayOfWeek: number;
  headcount: number;
}

export function useHeadcountForecast() {
  return useQuery({
    queryKey: ['statistics', 'headcount-forecast'],
    queryFn: () =>
      api.get<{ days: HeadcountDay[] }>('/api/statistics/headcount-forecast').then((r) => r.days),
  });
}

interface HoursPerEmployeeEntry {
  employee: { id: string; firstName: string; lastName: string; color: string; contractHours: number };
  actualHours: number;
  expectedHours: number;
  delta: number;
}

export function useHoursPerEmployee(period: 'past' | 'future') {
  return useQuery({
    queryKey: ['statistics', 'hours-per-employee', period],
    queryFn: () =>
      api
        .get<{ employees: HoursPerEmployeeEntry[]; period: string }>(
          `/api/statistics/hours-per-employee?period=${period}`
        )
        .then((r) => r.employees),
  });
}

interface MissionBreakdownEntry {
  mission: string;
  totalHours: number;
  percentage: number;
  employees: { employee: { id: string; firstName: string; lastName: string; color: string }; hours: number }[];
}

export function useMissionsBreakdown(period: 'past' | 'future') {
  return useQuery({
    queryKey: ['statistics', 'missions-breakdown', period],
    queryFn: () =>
      api
        .get<{ missions: MissionBreakdownEntry[]; totalHours: number; period: string }>(
          `/api/statistics/missions-breakdown?period=${period}`
        )
        .then((r) => r),
  });
}

// ============================================================================
// VALIDATION
// ============================================================================

interface WeekValidationEntry {
  year: number;
  week: number;
  startDate: string;
  endDate: string;
  status: 'PENDING' | 'VALIDATED' | 'FROZEN' | null;
  validatedAt: string | null;
  validatedTotalHours: number | null;
  validatedOvertimeHours: number | null;
  currentTotalHours: number;
  schedulesCount: number;
}

export function useWeeklyValidationAll() {
  return useQuery({
    queryKey: ['validation', 'all'],
    queryFn: () => api.get<{ weeks: WeekValidationEntry[] }>('/api/validation/all').then((r) => r.weeks),
  });
}

interface ValidationAdjustment {
  employeeId: string;
  date: string;
  status: string;
  morningStart?: string | null;
  morningEnd?: string | null;
  afternoonStart?: string | null;
  afternoonEnd?: string | null;
}

export interface WeekSignatureLite {
  employeeId: string;
  signedAt: string;
}

export function useWeeklyValidation(year: number, week: number) {
  return useQuery({
    queryKey: ['validation', 'week', year, week],
    queryFn: () =>
      api.get<{
        validation: { status: string; validatedAt: string | null; totalHours: number | null; overtimeHours: number | null } | null;
        signatures: WeekSignatureLite[];
      }>(`/api/validation?year=${year}&week=${week}`),
  });
}

/** Un salarié attendu à la signature qui n'a pas signé (corps du 409). */
export interface MissingSignature {
  id: string;
  firstName: string;
  lastName: string;
}

/**
 * Corps renvoyé par le serveur quand la semaine n'est pas signée par tout le
 * monde. C'est LUI qui nomme les salariés : l'écran ne les devine pas, parce
 * que seul le serveur sait qui a un compte et qui a gardé sa signature active.
 */
export interface MissingSignaturesError {
  error: string;
  code: 'MISSING_SIGNATURES';
  missing: MissingSignature[];
}

export function isMissingSignatures(body: unknown): body is MissingSignaturesError {
  return !!body && typeof body === 'object' && (body as any).code === 'MISSING_SIGNATURES';
}

export function useValidateWeek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      year: number;
      week: number;
      comment?: string;
      adjustments?: ValidationAdjustment[];
      /** Valider malgré des signatures manquantes (second appel, après confirmation). */
      force?: boolean;
    }) => api.post('/api/validation/validate', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['validation'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['shuttle'] });
    },
  });
}

export function useUnvalidateWeek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ year, week }: { year: number; week: number }) =>
      api.delete(`/api/validation/${year}/${week}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['validation'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['shuttle'] });
    },
  });
}

// ============================================================================
// SHUTTLE
// ============================================================================

export interface ShuttleAbsenceLine {
  type: string;
  motif: string;
  from: string;
  to: string;
  hoursToDeduct: number;
  comment: string | null;
}
export interface ShuttleLeaveLine { from: string; to: string; days: number }
export interface ShuttleEmployeeLine {
  employeeId: string;
  name: string;
  contractHours: number;
  contractMonthHours: number;
  workedHours: number;
  absenceHours: number;
  weeklyOvertime: Record<number, number>;
  totalOvertime: number;
  /** `totalOvertime + carriedIn`, ou la valeur déclarée à la main. */
  balance: number;
  carriedIn: number;
  toRecover: boolean;
  /** Solde cumulé à l'entrée du mois (tous les mois précédents). */
  balanceBefore: number;
  /** Ajustements manuels de solde saisis ce mois-ci (R4). */
  adjustmentsThisMonth: number;
  /** Solde cumulé à la sortie du mois : balanceBefore + net du mois + ajustements. */
  balanceAfter: number;
  leaves: ShuttleLeaveLine[];
  absences: ShuttleAbsenceLine[];
  primes: number;
  remarks: string;
  /** Cases déclarées à la main. Vide = tout vient du calcul. */
  overrides: ShuttleOverrides;
  /** Ce que le calcul proposait — sert à afficher « le calcul disait X » et à revenir en arrière. */
  computed: {
    weeklyOvertime: Record<number, number>;
    balance: number;
    leaves: ShuttleLeaveLine[];
    absences: ShuttleAbsenceLine[];
  };
}
/**
 * Cases forcées par le titulaire. Le calcul propose, il déclare.
 * Une clé absente = la case suit le calcul.
 */
export interface ShuttleOverrides {
  weeklyOvertime?: Record<string, number>;
  balance?: number;
  leaves?: ShuttleLeaveLine[];
  absences?: ShuttleAbsenceLine[];
}
export interface ShuttleData {
  pharmacy: { name: string; address: string | null };
  year: number;
  month: number;
  weeks: number[];
  lines: ShuttleEmployeeLine[];
}

export function useShuttleData(year: number, month: number) {
  return useQuery({
    queryKey: ['shuttle', 'data', year, month],
    queryFn: () =>
      api.get<{ data: ShuttleData }>(`/api/shuttle/data?year=${year}&month=${month}`).then((r) => r.data),
  });
}

export function useSaveShuttleLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      year: number; month: number; employeeId: string;
      primes?: number | null; remarks?: string | null;
      toRecover?: boolean; overtimeHours?: number;
      /**
       * Cases déclarées. Trois états par clé :
       *   absente → on n'y touche pas · `null` → retour au calcul · valeur → déclarée.
       * `overrides: null` remet toute la ligne au calcul.
       */
      overrides?: {
        weeklyOvertime?: Record<string, number> | null;
        balance?: number | null;
        leaves?: ShuttleLeaveLine[] | null;
        absences?: Omit<ShuttleAbsenceLine, 'type'>[] | null;
      } | null;
    }) => api.put('/api/shuttle/line', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shuttle', 'data'] }),
  });
}

export function useShuttleHistory() {
  return useQuery({
    queryKey: ['shuttle', 'history'],
    queryFn: () => api.get<{ reports: unknown[] }>('/api/shuttle/history').then((r) => r.reports),
  });
}

export function useGenerateShuttle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { year: number; month: number }) =>
      api.post<{ pdfUrl: string; report: unknown }>('/api/shuttle/generate', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shuttle'] }),
  });
}

export function useSignShuttle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { year: number; month: number }) =>
      api.post<{ pdfUrl: string; report: unknown; signedAt: string; signedByName: string }>('/api/shuttle/sign', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shuttle'] }),
  });
}

export function useSendShuttle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { year: number; month: number; sign?: boolean }) =>
      api.post<{ report: unknown; pdfUrl: string }>('/api/shuttle/send', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shuttle'] }),
  });
}

// ============================================================================
// INVITATION MOBILE (code d'activation salarié)
// ============================================================================

export interface EmployeeInvite {
  code: string;
  expiresAt: string;
  employee: { id: string; firstName: string; lastName: string };
}

/** Génère (ou rafraîchit) le code d'invitation mobile d'un salarié. */
export function useInviteEmployee() {
  return useMutation({
    mutationFn: (employeeId: string) =>
      api
        .post<{ invite: EmployeeInvite }>(`/api/employees/${employeeId}/invite`)
        .then((r) => r.invite),
  });
}

// ============================================================================
// DEMANDES DE CONGÉ (file de validation titulaire)
// ============================================================================

export interface PendingLeave {
  id: string;
  employeeId: string;
  startDate: string;
  endDate: string;
  daysCount: number;
  type: AbsenceType;
  reason: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  createdAt: string;
  employee: { firstName: string; lastName: string; position: EmployeePosition };
}

export function usePendingLeaves() {
  return useQuery({
    queryKey: ['leaves', 'pending'],
    queryFn: () =>
      api.get<{ requests: PendingLeave[] }>('/api/leaves').then((r) => r.requests),
  });
}

/** Congés déjà acceptés (pour révocation éventuelle). */
export function useApprovedLeaves() {
  return useQuery({
    queryKey: ['leaves', 'approved'],
    queryFn: () =>
      api.get<{ requests: PendingLeave[] }>('/api/leaves?status=APPROVED').then((r) => r.requests),
  });
}

/**
 * Valide une absence. La réponse indique s'il faut proposer au titulaire de
 * recalculer les horaires du personnel en mode VARIABLE : l'absence vient de
 * creuser un trou que quelqu'un pourrait combler.
 */
export interface ApproveLeaveResult {
  variableStaff: VariableStaffMember[];
  suggestVariableRecompute: boolean;
  recomputeRange: { from: string; to: string };
}

export function useApproveLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch<ApproveLeaveResult>(`/api/leaves/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leaves'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
    },
  });
}

/** Annule/révoque un congé, même après acceptation. */
export function useCancelLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch(`/api/leaves/${id}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leaves'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['absences'] });
    },
  });
}

export function useRejectLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewNote }: { id: string; reviewNote: string }) =>
      api.patch(`/api/leaves/${id}/reject`, { reviewNote }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leaves'] }),
  });
}

// ============================================================================
// CORRECTIONS D'HORAIRE (file de validation titulaire)
// ============================================================================

export interface PendingTimeAdjustment {
  id: string;
  employeeId: string;
  date: string;
  morningStart: string | null;
  morningEnd: string | null;
  afternoonStart: string | null;
  afternoonEnd: string | null;
  reason: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  createdAt: string;
  employee: { firstName: string; lastName: string; position: EmployeePosition };
}

export function usePendingTimeAdjustments() {
  return useQuery({
    queryKey: ['time-adjustments', 'pending'],
    queryFn: () =>
      api
        .get<{ requests: PendingTimeAdjustment[] }>('/api/time-adjustments')
        .then((r) => r.requests),
  });
}

/** Corrections déjà acceptées (pour révocation éventuelle). */
export function useApprovedTimeAdjustments() {
  return useQuery({
    queryKey: ['time-adjustments', 'approved'],
    queryFn: () =>
      api
        .get<{ requests: PendingTimeAdjustment[] }>('/api/time-adjustments?status=APPROVED')
        .then((r) => r.requests),
  });
}

export function useApproveTimeAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch(`/api/time-adjustments/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['time-adjustments'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

/** Annule/révoque une correction, même après acceptation. */
export function useCancelTimeAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch(`/api/time-adjustments/${id}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['time-adjustments'] });
      qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useRejectTimeAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewNote }: { id: string; reviewNote: string }) =>
      api.patch(`/api/time-adjustments/${id}/reject`, { reviewNote }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['time-adjustments'] }),
  });
}

// ============================================================================
// NOTIFICATIONS (salarié)
// ============================================================================

export interface Notification {
  id: string;
  type: 'SCHEDULE_CHANGED' | 'GENERAL';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

export function useMyNotifications() {
  return useQuery({
    queryKey: ['my-notifications'],
    queryFn: () =>
      api.get<{ notifications: Notification[]; unreadCount: number }>('/api/my/notifications'),
    refetchInterval: 60_000, // rafraîchit toutes les minutes
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) =>
      api.patch('/api/my/notifications/read', ids ? { ids } : {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-notifications'] }),
  });
}

// ============================================================================
// AJUSTEMENTS D'HEURES (R4 — solde manuel, onglet Compteurs)
// ============================================================================

export interface HourAdjustment {
  id: string;
  hours: number;
  reason: string | null;
  effectiveDate: string;
  createdAt: string;
}

export function useHourAdjustments(id: string | undefined) {
  return useQuery({
    queryKey: ['employees', id, 'hour-adjustments'],
    enabled: !!id,
    queryFn: () =>
      api.get<{ adjustments: HourAdjustment[]; total: number }>(`/api/employees/${id}/hour-adjustments`),
  });
}

export function useAddHourAdjustment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { hours: number; reason?: string | null; effectiveDate?: string }) =>
      api.post(`/api/employees/${id}/hour-adjustments`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees', id, 'hour-adjustments'] });
      qc.invalidateQueries({ queryKey: ['employees', id, 'counters'] });
    },
  });
}

export function useDeleteHourAdjustment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (adjId: string) =>
      api.delete(`/api/employees/${id}/hour-adjustments/${adjId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees', id, 'hour-adjustments'] });
      qc.invalidateQueries({ queryKey: ['employees', id, 'counters'] });
    },
  });
}

// Journal d'événements d'un salarié (côté titulaire — onglet Historique).
export function useEmployeeHistory(id: string | undefined) {
  return useQuery({
    queryKey: ['employee-history', id],
    enabled: !!id,
    queryFn: () =>
      api.get<{ events: Notification[] }>(`/api/employees/${id}/history`).then((r) => r.events),
  });
}

// ============================================================================
// SIMULATION (R3 — onglet « Simulation » côté titulaire)
// ============================================================================

/** Portée de l'écran « Changer les horaires » : l'officine active, ou tout le groupe. */
export type SimScope = 'site' | 'group';

export interface SimDraft {
  employeeId: string;
  firstName: string;
  lastName: string;
  color: string;
  position: EmployeePosition;
  scheduleMode: ScheduleMode;
  /** Pharmacie de rattachement (celle qui porte le contrat). */
  homeSiteId: string;
  /** Toutes les officines où il apparaît — rattachement compris. */
  siteIds: string[];
  /** Période du contrat : pas d'horaires avant l'embauche ni après la fin. */
  hireDate: string;
  endDate: string | null;
  fixedDayOff: number | null;
  fixedDayOff2: number | null;
  rows: WeeklyTemplateDay[];
}

export interface SimCell {
  status: string;
  absenceType: string | null;
  morningStart: string | null;
  morningEnd: string | null;
  afternoonStart: string | null;
  afternoonEnd: string | null;
  /** L'officine à qui appartient la journée — et son matin (multi-site). */
  siteId: string;
  /**
   * L'officine de l'après-midi. Vaut `siteId` tant que la journée ne se partage
   * pas : l'écran peut donc toujours la lire.
   */
  afternoonSiteId: string;
}

export interface SimPreview {
  from: string;
  to: string;
  employees: {
    id: string; firstName: string; lastName: string; color: string;
    position: EmployeePosition; homeSiteId: string;
    hireDate: string; endDate: string | null;
  }[];
  days: {
    date: string;
    weekday: number;
    cells: Record<string, SimCell>;
  }[];
  /** Les officines couvertes par l'aperçu, dans l'ordre d'affichage. */
  sites: { id: string; name: string; city: string | null }[];
}

type SimDraftPayload = Pick<SimDraft, 'employeeId' | 'scheduleMode' | 'fixedDayOff' | 'fixedDayOff2' | 'rows'>;

export function useSimulationDrafts(scope: SimScope = 'site') {
  return useQuery({
    queryKey: ['simulation-drafts', scope],
    queryFn: () =>
      api.get<{
        drafts: SimDraft[];
        sites: { id: string; name: string; city: string | null }[];
        activeSiteId: string;
      }>(`/api/simulation/drafts?scope=${scope}`),
  });
}

export function useSimulationPreview() {
  return useMutation({
    mutationFn: (body: {
      drafts: SimDraftPayload[];
      startDate?: string;
      keepExceptions: boolean;
      scope?: SimScope;
      days?: number;
    }) => api.post<SimPreview>('/api/simulation/preview', body),
  });
}

/**
 * Ce que l'application a VRAIMENT fait. Le détail compte : « ça en a fait 90 %,
 * mais pas tout » venait de jours qu'on ne pouvait pas écrire sans le dire.
 */
export interface ApplyResult {
  applied: number;
  mode: string;
  upserted: number;
  /** Jours qui étaient figés à la main et qui repassent sous la semaine type. */
  clearedManual?: number;
  /** Jours qu'on n'a PAS pu changer : la semaine est déjà validée. */
  lockedDays?: number;
}

export function useApplySimulation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      drafts: SimDraftPayload[];
      mode: 'permanent' | 'temporary';
      startDate: string;
      endDate?: string;
      keepExceptions: boolean;
      scope?: SimScope;
    }) =>
      api.post<ApplyResult>('/api/simulation/apply', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['simulation-drafts'] });
    },
  });
}

// ============================================================================
// HORAIRES VARIABLES — comblement automatique des creux
// ============================================================================
// Un salarié en mode VARIABLE n'a pas d'horaires fixes : le moteur le place sur
// les trous de couverture. Le titulaire garde la main : on lui montre d'abord
// un APERÇU (aucune écriture), il coche qui recalculer, puis on applique.

/** Les salariés en horaires variables — sert à savoir s'il faut proposer un recalcul. */
/**
 * Personnel en horaires variables. `enabled` sert au poste comptoir : cette
 * route lui est fermée (liste blanche), la lancer ne rapporterait qu'un 403.
 */
export function useVariableStaff(enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['variable-staff'],
    queryFn: () =>
      api.get<{ employees: VariableStaffMember[] }>('/api/schedules/variable-staff')
        .then((r) => r.employees),
  });
}

/**
 * Aperçu du comblement : qui viendrait, quand, combien d'heures ça ajoute.
 * N'écrit rien — c'est ce qui alimente la question « voulez-vous recalculer ? ».
 */
export function usePreviewVariableFill() {
  return useMutation({
    mutationFn: (body: { from: string; to: string; employeeIds?: string[] }) =>
      api.post<{ plan: VariableFillPlan }>('/api/schedules/variable-fill/preview', body)
        .then((r) => r.plan),
  });
}

/**
 * Applique le comblement aux salariés cochés, puis notifie ceux dont le planning
 * a réellement bougé (accusé de réception demandé côté mobile).
 */
export function useApplyVariableFill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { from: string; to: string; employeeIds: string[] }) =>
      api.post<{
        plan: VariableFillPlan;
        result: { written: number; cleared: number; skippedLocked: number; changedEmployeeIds: string[] };
        notified: number;
      }>('/api/schedules/variable-fill/apply', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['coverage'] });
      qc.invalidateQueries({ queryKey: ['organization'] });
      qc.invalidateQueries({ queryKey: ['pharmacy-notifications'] });
    },
  });
}

// ============================================================================
// ACCUSÉS DE RÉCEPTION (vue titulaire)
// ============================================================================

/**
 * Les notifications envoyées à l'équipe et leur état d'accusé de réception.
 * Le titulaire doit pouvoir vérifier qui n'a pas encore vu son nouveau planning.
 */
export function usePharmacyNotifications(pendingAckOnly = true) {
  return useQuery({
    queryKey: ['pharmacy-notifications', pendingAckOnly],
    queryFn: () =>
      api.get<{ notifications: AppNotification[]; pendingAckCount: number }>(
        `/api/notifications?pendingAckOnly=${pendingAckOnly}`
      ),
    refetchInterval: 120_000,
  });
}

// ============================================================================
// BALANCE DES HEURES — tableau des 12 derniers mois
// ============================================================================
// Une ligne par salarié, une colonne par mois, plus le solde de départ et la
// balance globale. Mêmes règles de calcul que la fiche navette.

export interface BalanceMonth {
  year: number;
  month: number;
  key: string;   // "2026-07"
  label: string; // « juil. 26 »
}

export interface BalanceLine {
  employeeId: string;
  name: string;
  color: string;
  contractHours: number;
  isOwner: boolean;
  /** Solde repris à l'ouverture du compte (saisi par le titulaire). */
  openingHoursBalance: number;
  monthly: Record<string, number>;
  /** Ajustements manuels du mois — déjà inclus dans `monthly`. */
  adjustments: Record<string, number>;
  /**
   * Cases déclarées à la main, qui remplacent le calcul.
   * Clé « YYYY-MM » pour un mois, `BALANCE_TOTAL_CELL` pour la balance globale.
   * Clé absente = la case vient du calcul.
   */
  declared: Record<string, number>;
  /** Ce que le calcul proposait — pour dire « le calcul disait X ». */
  computed: {
    monthly: Record<string, number>;
    total: number;
  };
  /** Somme des colonnes affichées uniquement. */
  periodTotal: number;
  /** Départ + tout l'historique connu. */
  total: number;
}

/** La case « Balance globale » dans `declared` (miroir de `TOTAL_CELL` côté serveur). */
export const BALANCE_TOTAL_CELL = 'total';

export function useHoursBalance(months = 12) {
  return useQuery({
    queryKey: ['hours-balance', months],
    queryFn: () =>
      api.get<{ months: BalanceMonth[]; lines: BalanceLine[] }>(
        `/api/statistics/hours-balance?months=${months}`
      ),
  });
}

/** Solde de départ d'un salarié (reprise de l'existant à l'installation). */
export function useSetOpeningBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, hours }: { employeeId: string; hours: number }) =>
      api.patch(`/api/employees/${employeeId}`, { openingHoursBalance: hours }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['hours-balance'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employees', v.employeeId] });
      qc.invalidateQueries({ queryKey: ['shuttle'] });
    },
  });
}

/**
 * Déclarer une case de la balance — un mois, ou la balance globale.
 *
 * Le calcul n'est jamais écrasé : la déclaration se pose à côté, et
 * `useResetBalanceCell` la retire. C'est ce qui remplace l'ancienne
 * « correction », qui enregistrait un ajustement daté : un ajustement se
 * propage (mois de sa date d'effet, fiche navette, corrections vues par le
 * salarié) et ne se défait pas d'un clic. Pour en enregistrer un vrai —
 * ce qui reste le bon geste quand des heures ont réellement été données ou
 * reprises — c'est dans la fiche du salarié, onglet « Compteurs ».
 */
function useBalanceCellMutation<V extends { employeeId: string }>(fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hours-balance'] });
    },
  });
}

/** Poser une valeur déclarée sur une case. */
export function useDeclareBalanceCell() {
  return useBalanceCellMutation(({ employeeId, cell, hours }: { employeeId: string; cell: string; hours: number }) =>
    api.put(`/api/hours-balance/${employeeId}/${cell}`, { hours })
  );
}

/** « Revenir au calcul » : retirer la déclaration d'une case. */
export function useResetBalanceCell() {
  return useBalanceCellMutation(({ employeeId, cell }: { employeeId: string; cell: string }) =>
    api.delete(`/api/hours-balance/${employeeId}/${cell}`)
  );
}

// ---------------------------------------------------------------------------
// Réinitialisation de la pharmacie — DESTRUCTIF
// ---------------------------------------------------------------------------

export interface ResetPreview {
  pharmacyName: string;
  counts: {
    employees: number;
    schedules: number;
    absences: number;
    appointments: number;
    shuttleReports: number;
  };
  multiSite: boolean;
  sitesInGroup: number;
}

/**
 * Ce que la réinitialisation détruirait, compté sur les vraies données.
 * `staleTime: 0` : ces chiffres doivent être ceux de l'instant, pas ceux d'un
 * cache — c'est sur eux qu'on décide de tout effacer.
 */
export function useResetPreview(enabled: boolean) {
  return useQuery({
    queryKey: ['pharmacy', 'reset-preview'],
    queryFn: () => api.get<ResetPreview>('/api/pharmacy/reset-preview'),
    enabled,
    staleTime: 0,
    retry: false,
  });
}

/** Efface la pharmacie et tout ce qui s'y rattache. Sans retour possible. */
export function useResetPharmacy() {
  return useMutation({
    mutationFn: ({ confirmation }: { confirmation: string }) =>
      api.post<{ success: true; message: string }>('/api/pharmacy/reset', { confirmation }),
  });
}
